import { useEffect, useState } from "react";
import { api } from "@/lib/firebase/api-client";
import { photoReduite } from "@/lib/utils/photo";

/**
 * ===========================================================================
 *  PRIX DU KILO DE LA VOLAILLE ACHETEE EN CAISSE (18/10/2026)
 * ===========================================================================
 *
 *  A Oulfa, en attendant un volailler, le responsable commande achete les
 *  poulets en caisse. Chaque matin il donne le prix du kilo (beldi, roumi),
 *  comme le ferait un volailler : la fenetre s'ouvre seule tant que rien
 *  n'est saisi aujourd'hui, puis reste a portee par un bouton.
 *
 *  Ensuite, a chaque commande, il pese le poulet dans « à acheter » : ce qui
 *  sort de la caisse = poids x prix du kilo, et le prix client est recalcule.
 *
 *  Rien ne s'affiche dans un magasin sans volaille achetee en caisse.
 * ===========================================================================
 */

type Etat = Awaited<ReturnType<typeof api.orders.prixCaisseDuJour>>;
type Ligne = {
  id: string;
  nom: string;
  nomAr?: string | null;
  photo?: string | null;
  ref?: string | null;
  refNom?: string | null;
  articles?: string[];
};

export default function PrixVolailleCaisse({ onEnregistre }: { onEnregistre?: () => void }) {
  const [etat, setEtat] = useState<Etat | null>(null);
  const [saisie, setSaisie] = useState<Record<string, string>>({});
  const [ouvertMain, setOuvertMain] = useState(false);
  const [envoi, setEnvoi] = useState(false);
  const [erreur, setErreur] = useState<string | null>(null);

  function charger() {
    api.orders
      .prixCaisseDuJour()
      .then((r) => {
        setEtat(r);
        const pre: Record<string, string> = {};
        for (const a of [...(r.articles || []), ...(r.references || [])]) {
          const v = a.prixKg ?? a.dernier;
          pre[a.id] = v ? String(v) : "";
        }
        setSaisie(pre);
      })
      .catch(() => setEtat(null));
  }

  useEffect(() => {
    charger();
  }, []);

  if (!etat?.auPoids) return null;

  const ouvert =
    ouvertMain || (etat.aJour === false && Object.keys(etat.prix || {}).length === 0);

  async function enregistrer() {
    const lies = new Set((etat?.articles || []).filter((a) => a.ref).map((a) => a.id));
    const prix: Record<string, number> = {};
    for (const [id, v] of Object.entries(saisie)) {
      if (lies.has(id)) continue;
      const n = Number(String(v).replace(",", "."));
      if (n > 0) prix[id] = n;
    }
    if (Object.keys(prix).length === 0) {
      setErreur("Entre au moins un prix du kilo.");
      return;
    }
    try {
      setEnvoi(true);
      setErreur(null);
      await api.orders.enregistrerPrixCaisseDuJour(prix);
      setOuvertMain(false);
      charger();
      onEnregistre?.();
    } catch (e: any) {
      setErreur(e?.message || "Enregistrement impossible.");
    } finally {
      setEnvoi(false);
    }
  }

  const ligne = (a: Ligne, marche: boolean) => {
    const lie = !marche && a.ref ? a.ref : null;
    const valeur = lie ? saisie[lie] : saisie[a.id];
    return (
      <div
        key={a.id}
        className={`flex items-center gap-3 p-2 rounded-xl border ${
          valeur ? (lie ? "border-gray-100 bg-gray-50" : "border-gray-200") : "border-red-300 bg-red-50"
        }`}
      >
        {a.photo ? (
          <img src={photoReduite(a.photo, 96)} alt="" className="w-12 h-12 rounded-lg object-cover flex-shrink-0" />
        ) : (
          <div className="w-12 h-12 rounded-lg bg-amber-100 flex items-center justify-center text-2xl flex-shrink-0">
            🐔
          </div>
        )}
        <p className="flex-1 text-sm font-bold text-gray-900 leading-tight">
          {a.nom}
          {a.nomAr && <span className="block text-[11px] font-normal text-gray-500" dir="rtl">{a.nomAr}</span>}
          {marche && a.articles && a.articles.length > 0 && (
            <span className="block text-[11px] font-normal text-emerald-700">
              fixe le prix de : {a.articles.join(", ")}
            </span>
          )}
          {lie && (
            <span className="block text-[11px] font-normal text-emerald-700">= prix du {a.refNom || "marché"}</span>
          )}
        </p>
        {lie ? (
          <span className="w-24 px-2 py-2 text-lg font-bold text-gray-500 text-center">{valeur || "—"}</span>
        ) : (
          <input
            type="number"
            inputMode="decimal"
            step="0.5"
            min="0"
            value={saisie[a.id] ?? ""}
            onChange={(e) => setSaisie((p) => ({ ...p, [a.id]: e.target.value }))}
            className="w-24 px-2 py-2 text-lg font-bold border-2 border-gray-300 rounded-lg text-center"
            placeholder="DH/kg"
          />
        )}
      </div>
    );
  };

  return (
    <>
      <div className="px-3 md:px-6 mb-3">
        <button
          type="button"
          onClick={() => setOuvertMain(true)}
          className="inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-amber-50 border border-amber-300 text-amber-800 font-bold text-sm"
        >
          ⚖️ Volaille caisse — prix du kilo aujourd'hui · ثمن الكيلو
          {etat.manquants ? (
            <span className="px-2 py-0.5 rounded-full bg-red-600 text-white text-xs">{etat.manquants}</span>
          ) : (
            <span className="text-emerald-600">✓</span>
          )}
        </button>
      </div>

      {ouvert && (
        <div className="fixed inset-0 z-[60] bg-black/60 flex items-end md:items-center justify-center p-0 md:p-4">
          <div className="bg-white w-full md:max-w-lg max-h-[92vh] rounded-t-3xl md:rounded-3xl shadow-2xl flex flex-col">
            <div className="p-5 bg-gradient-to-r from-amber-500 to-orange-500 rounded-t-3xl text-white relative">
              {Object.keys(etat.prix || {}).length > 0 && (
                <button
                  type="button"
                  onClick={() => setOuvertMain(false)}
                  className="absolute right-4 top-4 w-9 h-9 rounded-full bg-white/25 text-white text-xl font-black"
                  aria-label="Fermer"
                >
                  ×
                </button>
              )}
              <p className="text-2xl font-black">Volaille caisse ⚖️</p>
              <p className="mt-1 text-base font-semibold">
                Le prix du kilo payé aujourd'hui au marché, en dirhams.
              </p>
              <p className="mt-0.5 text-sm" dir="rtl">ثمن الكيلو ديال اليوم فالسوق (بالدرهم)</p>
            </div>
            <div className="flex-1 overflow-y-auto p-4 space-y-2">
              {(etat.references || []).length > 0 && (
                <>
                  <p className="text-sm font-black text-gray-700">Prix du marché</p>
                  {(etat.references || []).map((a) => ligne(a, true))}
                </>
              )}
              <p className="pt-3 text-sm font-black text-gray-700">Articles</p>
              {(etat.articles || []).map((a) => ligne(a, false))}
              {erreur && <p className="text-sm font-semibold text-red-700">{erreur}</p>}
            </div>
            <div className="p-4 border-t border-gray-200">
              <button
                type="button"
                onClick={() => void enregistrer()}
                disabled={envoi}
                className="w-full py-4 bg-emerald-600 text-white text-lg font-black rounded-2xl disabled:opacity-50"
              >
                {envoi ? "…" : "Valider les prix du jour · تأكيد"}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
